export const MANAGER_REPORT_BASE_PATH = "/manager/report";

const REPORT_META = {
  invoices: {
    title: "Invoices",
    description: "Review invoice activity, narrow the date range, and open invoice details without leaving the reporting workspace.",
  },
  "supplier-redemption": {
    title: "Supplier Redemption",
    description: "Track supplier redemption activity, refine the merchant selection, and export the current result set when needed.",
  },
  "participant-account-summary": {
    title: "Participant Account Summary",
    description: "Review participant balances, awarded totals, and redemption activity across the selected program scope.",
  },
  "deposit-transfers": {
    title: "Deposit Transfers",
    description: "Monitor transfer activity, narrow the reporting window, and export the filtered transfer history.",
  },
  "program-status": {
    title: "Program Status",
    description: "Review program-level status changes, apply date filters, and export the current report view.",
  },
  "annual-awards-summary": {
    title: "Annual Awards Summary",
    description: "Compare award activity against budget across the selected month and year without leaving the report flow.",
  },
  "referral-participant": {
    title: "Referral Participant",
    description: "Review referral engagement activity, narrow the selected programs, and export the filtered results.",
  },
  "award-account-summary-gl": {
    title: "Award Account Summary GL",
    description: "Review general ledger award account activity with compact filters, exports, and report tables.",
  },
  "award-detail": {
    title: "Award Detail",
    description: "Inspect detailed award records, refine the date range, and export the active result set.",
  },
  "peer-to-peer": {
    title: "Peer to Peer",
    description: "Review peer-to-peer recognition activity, apply filters, and export the current report slice.",
  },
  "award-summary": {
    title: "Award Summary",
    description: "Summarize award activity across the selected scope with consistent filters, exports, and report tables.",
  },
  "file-import": {
    title: "File Import",
    description: "Review import activity, filter the reporting window, and export the current import history.",
  },
  "merchant-redemption": {
    title: "Merchant Redemption",
    description: "Track merchant redemption activity, filter the selected programs, and export the filtered results.",
  },
  "quarterly-awards-summary": {
    title: "Quarterly Awards Summary",
    description: "Review quarterly award performance with compact filters and export controls aligned to the current report.",
  },
  "participant-status-summary": {
    title: "Participant Status Summary",
    description: "Review participant status changes, refine the program scope, and export the current report data.",
  },
  "deposit-balance": {
    title: "Deposit Balance",
    description: "Monitor deposit balances across the selected programs with compact filters and export controls.",
  },
  "goal-progress-summary": {
    title: "Goal Progress Summary",
    description: "Review goal progress across the selected scope and export the filtered report output when needed.",
  },
  "claim-award-summary": {
    title: "Claim Award Summary",
    description: "Track claim-award activity with compact filters, modern tables, and export controls.",
  },
  "cascading-approval-summary": {
    title: "Approval Summary",
    description: "Review cascading approval activity, refine the selected filters, and export the current report state.",
  },
  "scheduled-award-summary": {
    title: "Scheduled Award Summary",
    description: "Review scheduled award activity by date and program, then export the current results as needed.",
  },
  "schedule-awards-summary": {
    title: "Schedule Awards Summary",
    description: "Review cascading-approval awards with a scheduled date, refine filters, and export the current report state.",
  },
  "team-roster-summary": {
    title: "Team Roster Summary",
    description: "Review team roster activity in a compact report layout with aligned filters and exports.",
  },
  "cocard-award-detail": {
    title: "Cocard Award Detail",
    description: "Inspect Cocard award records with consistent filters, exports, and dense report tables.",
  },
  "cocard-three-percent-fee-invoices": {
    title: "Cocard Three Percent Fee Invoices",
    description: "Review Cocard fee invoices, switch report periods quickly, and open invoice details from the same workflow.",
  },
  "cocard-participant-redemption": {
    title: "Cocard Participant Redemption",
    description: "Track Cocard participant redemption activity with compact filters and export controls.",
  },
  redemption: {
    title: "Redemption",
    description: "Review redemption activity across the selected programs and export the current result set when needed.",
  },
  "award-approvals-history": {
    title: "Award Approvals History",
    description: "Review approval history data, track lease approvals and rejections, and export the current report view.",
  },
  "pending-holds": {
    title: "Pending Holds",
    description: "See every deposit and redemption amount currently pending or on hold for this program and its sub-programs, with totals by program and overall.",
  },
};

const LEGACY_REPORT_SLUGS = {
  "goal-progress-summay": "goal-progress-summary",
  "team-roaster-summary": "team-roster-summary",
};

const titleCaseFromSlug = (slug = "") =>
  String(slug)
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const slugifyReportLabel = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

export const normalizeManagerReportSlug = (value = "") => {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return "";
  }

  let nextValue = rawValue;

  if (nextValue.startsWith(MANAGER_REPORT_BASE_PATH)) {
    nextValue = nextValue.slice(MANAGER_REPORT_BASE_PATH.length);
  }

  nextValue = nextValue.replace(/^\/+|\/+$/g, "");
  nextValue = nextValue.split("/")[0] || "";
  nextValue = slugifyReportLabel(nextValue);

  return LEGACY_REPORT_SLUGS[nextValue] || nextValue;
};

export const getManagerReportMeta = (value = "") => {
  const slug = normalizeManagerReportSlug(value);
  const meta = REPORT_META[slug] || {};
  const title = meta.title || titleCaseFromSlug(slug || "report");

  return {
    slug,
    path: slug ? `${MANAGER_REPORT_BASE_PATH}/${slug}` : MANAGER_REPORT_BASE_PATH,
    title,
    description:
      meta.description ||
      `Review ${title.toLowerCase()} data, refine the current filters, and export the active report view as needed.`,
  };
};

export const normalizeManagerReportType = (reportType = {}) => {
  const fallbackSource = reportType.link || reportType.name || "";
  const meta = getManagerReportMeta(fallbackSource);

  return {
    ...reportType,
    link: meta.slug,
    name: meta.title,
    title: meta.title,
    slug: meta.slug,
    path: meta.path,
    description: meta.description,
  };
};

export const matchesManagerReportPath = (pathname, value = "") => {
  const { path } = getManagerReportMeta(value);
  return pathname === path || pathname.startsWith(`${path}/`);
};
